import {
  DateWindow,
  FraudByFicCategoryReportDataByDateWindow,
  toDisplayAmount,
} from "../../../../api/tenantInternalReports";
import {
  MergedHeadersSet,
  ReportTableColumn,
} from "../../../../components/ui/reportTable";
import { cn } from "../../../../lib/utils";
import { pickWindowData } from "../../logic/reportWindows";

export interface FraudByFicCategoryTableRow {
  serial_number: string;
  category: string;
  data_by_date_windows: FraudByFicCategoryReportDataByDateWindow[];
}

const DIVIDER = "border-l border-l-[#E5E5E5]";
const CATEGORY_CELL =
  "h-[52px] px-2 py-2 text-sm text-left whitespace-nowrap text-[#0A0A0A] font-medium";
const CATEGORY_NAME_CELL =
  "h-[52px] px-2 py-2 text-sm text-left whitespace-normal text-[#0A0A0A] font-medium max-w-[360px]";
const DATA_CELL =
  "h-[52px] px-2 py-2 text-sm text-right whitespace-nowrap text-[#0A0A0A] font-normal";
const CATEGORY_HEADER =
  "h-14 px-2 align-center text-left text-sm font-semibold whitespace-nowrap text-[#374151] border-b border-[#E5E5E5]";
const DATA_HEADER =
  "h-14 px-2 align-center text-right text-sm font-semibold whitespace-nowrap text-[#374151] border-b border-[#E5E5E5]";
const TIER1_PILL_CELL = "p-1 align-bottom";
const TIER1_PILL =
  "flex h-14 items-center justify-center rounded-lg px-4 text-sm font-semibold bg-[#F5F5F4] text-[#374151]";

const tier1Pill = (label: string) => <div className={TIER1_PILL}>{label}</div>;

const formatCount = (value: number | null) =>
  value == null ? "-" : value.toLocaleString("en-IN");

const formatAmount = (rawValue: number | null) => {
  const value = toDisplayAmount(rawValue);
  return value == null
    ? "-"
    : value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

export const buildFraudByFicCategoryColumns = (
  currentFyWindow: DateWindow,
  previousFyWindow: DateWindow
): ReportTableColumn<FraudByFicCategoryTableRow>[] => [
  {
    headerLabel: "Sr. No.",
    headerClassName: cn(CATEGORY_HEADER, "w-16"),
    getValue: (row) => row.serial_number,
    columnCellClassName: CATEGORY_CELL,
  },
  {
    headerLabel: "Category",
    headerClassName: cn(CATEGORY_HEADER, "min-w-[260px]"),
    getValue: (row) => row.category,
    columnCellClassName: CATEGORY_NAME_CELL,
  },
  {
    headerLabel: "Cases",
    headerClassName: DATA_HEADER,
    getValue: (row) =>
      formatCount(pickWindowData(row.data_by_date_windows, currentFyWindow)?.num_cases ?? null),
    columnCellClassName: DATA_CELL,
  },
  {
    headerLabel: "Amount Involved",
    headerClassName: DATA_HEADER,
    getValue: (row) =>
      formatAmount(pickWindowData(row.data_by_date_windows, currentFyWindow)?.total_amount ?? null),
    columnCellClassName: DATA_CELL,
  },
  {
    headerLabel: "Cases",
    headerClassName: cn(DATA_HEADER, DIVIDER),
    getValue: (row) =>
      formatCount(pickWindowData(row.data_by_date_windows, previousFyWindow)?.num_cases ?? null),
    columnCellClassName: cn(DATA_CELL, DIVIDER),
  },
  {
    headerLabel: "Amount Involved",
    headerClassName: DATA_HEADER,
    getValue: (row) =>
      formatAmount(pickWindowData(row.data_by_date_windows, previousFyWindow)?.total_amount ?? null),
    columnCellClassName: DATA_CELL,
  },
];

export const buildFraudByFicCategoryMergedHeadersSets = (
  currentFyLabel: string,
  previousFyLabel: string
): MergedHeadersSet[] => [
  {
    mergedColumns: [
      {
        label: tier1Pill(currentFyLabel),
        startColumnIndex: 2,
        endColumnIndex: 3,
        className: TIER1_PILL_CELL,
      },
      {
        label: tier1Pill(previousFyLabel),
        startColumnIndex: 4,
        endColumnIndex: 5,
        className: TIER1_PILL_CELL,
      },
    ],
  },
];
